import type {
  CaseSizeRuleListResponseType,
  SupplierGetResponseType,
} from "@sellar-io/api-v2/types";
import {
  formatCurrency,
  getContainerTypes,
  getItemName,
  getOrderItemCountOfContainerType,
  groupItemsByCaseSizeRules,
  pluralize,
} from "@sellar-io/common";
import { generatePriceTierOrderRuleDescription } from "~/utils/sellarHelpers";

export type OrderRequirementItem = {
  quantity: number;
  containerType: string | null;
  volume: number | null;
  unit: string | null;
  packQuantity: number | null;
};

/** Items and subtotal belong to one supplier, never the whole consolidated basket. */
export type OrderRequirementsBasket = {
  items: OrderRequirementItem[];
  subtotal: number;
};

type QuantityRule =
  SupplierGetResponseType["CustomerZones"][number]["PriceTier"]["PriceTierOrderRules"][number];
type CaseSizeRules = Pick<
  CaseSizeRuleListResponseType[number],
  "quantity" | "containerType" | "volume" | "unit"
>[];

type RequirementState = "description" | "remaining" | "met";
export type OrderRequirement = {
  key: string;
  state: RequirementState;
  isMet: boolean;
  percentageComplete: number;
  description: string;
  remainingText: string;
  metText: string;
};

const getRemainingItemName = (rule: QuantityRule, remaining: number) => {
  switch (rule.itemType) {
    case "any":
      return pluralize("item", remaining);
    case "can":
      return `${pluralize("case", remaining)} of cans`;
    case "bottle":
      return `${pluralize("case", remaining)} of bottles`;
    case "draught":
      return pluralize("draught item", remaining);
    case "bagInBox":
      return `bag in ${pluralize("box", remaining)}`;
    default:
      return `${pluralize(rule.itemType, remaining)}${rule.unit && rule.volume ? ` over ${rule.volume}${rule.unit}` : ""}`;
  }
};

const getMinimumOrderQuantityRequirement = ({
  rule,
  orderItemList,
  caseSizeRuleList,
}: {
  rule: QuantityRule;
  orderItemList: OrderRequirementItem[];
  caseSizeRuleList: CaseSizeRules;
}) => {
  const matchesContainerSize = (item: OrderRequirementItem) => {
    if (rule.unit && rule.volume) {
      return item.unit === rule.unit && (item.volume ?? 0) >= rule.volume;
    }
    return true;
  };
  const isApplicable =
    orderItemList.some(
      (item) =>
        getContainerTypes(item.containerType).includes(rule.itemType) &&
        matchesContainerSize(item),
    ) ||
    (orderItemList.length > 0 && rule.itemType === "any");

  const groupedItems = groupItemsByCaseSizeRules({
    caseSizeRuleList,
    orderItemList,
  });
  const quantity = groupedItems.reduce((total, item) => {
    if (item.containerType && matchesContainerSize(item)) {
      return (
        total +
        Math.floor(
          getOrderItemCountOfContainerType({
            containerType: rule.itemType,
            orderItemList: [item],
          }),
        )
      );
    }
    return total;
  }, 0);
  const isMet = quantity >= rule.quantity;
  const percentageComplete = isApplicable
    ? Math.max(Math.floor((quantity / rule.quantity) * 100), 1)
    : 0;
  const itemTypeName =
    rule.itemType === "any"
      ? pluralize("order item", rule.quantity)
      : getItemName({
          itemType: rule.itemType,
          quantity: rule.quantity,
          extended: true,
        });
  const remaining = rule.quantity - quantity;
  const remainingItemName = getRemainingItemName(rule, remaining);
  let state: RequirementState = "description";
  if (isApplicable) {
    state = isMet ? "met" : "remaining";
  }
  const remainingText = `Add ${remaining} more ${remainingItemName}. Minimum of ${rule.quantity} per order`;
  return {
    state,
    isApplicable,
    isMet,
    percentageComplete,
    remaining,
    remainingItemName,
    itemTypeName,
    description: generatePriceTierOrderRuleDescription(rule),
    remainingText,
    metText: `Minimum order of ${rule.quantity} ${itemTypeName} met!`,
  };
};

export const useMinimumOrderQuantityRequirement =
  getMinimumOrderQuantityRequirement;

export const useMinimumOrderPriceRequirement = ({
  minimumOrderPrice,
  price,
  currency,
}: {
  minimumOrderPrice: number;
  price?: number;
  currency: SupplierGetResponseType["currency"];
}) => {
  const isMet = (price ?? 0) >= minimumOrderPrice;
  let state: RequirementState = "description";
  if (price) {
    state = isMet ? "met" : "remaining";
  }
  return {
    state,
    isMet,
    percentageComplete:
      price && minimumOrderPrice > 0 ? (price / minimumOrderPrice) * 100 : 0,
    description: `Minimum order total of ${formatCurrency(minimumOrderPrice, currency)}`,
    remainingText: `Spend ${formatCurrency(minimumOrderPrice - (price ?? 0), currency)} more to meet minimum order total`,
    metText: `Minimum order total of ${formatCurrency(minimumOrderPrice, currency)} met!`,
  };
};

export const usePriceTierOrderRequirements = ({
  priceTierOrderRules = [],
  minimumOrderPrice,
  caseSizeRuleList = [],
  currency,
  basket,
}: {
  priceTierOrderRules?: QuantityRule[];
  minimumOrderPrice?: number | null;
  caseSizeRuleList?: CaseSizeRules;
  currency: SupplierGetResponseType["currency"];
  basket?: OrderRequirementsBasket;
}) => {
  const minimumSpend = useMinimumOrderPriceRequirement({
    minimumOrderPrice: minimumOrderPrice ?? 0,
    price: basket?.subtotal,
    currency,
  });
  const quantityRequirements = priceTierOrderRules.map((rule) => ({
    ...getMinimumOrderQuantityRequirement({
      rule,
      orderItemList: basket?.items ?? [],
      caseSizeRuleList,
    }),
    key: `quantity-${rule.id}`,
  }));
  const requirements: OrderRequirement[] = [...quantityRequirements];
  if (minimumOrderPrice) {
    requirements.unshift({ ...minimumSpend, key: "minimum-spend" });
  }
  return requirements;
};
